/* GS://PROTOCOLO
 * Arquivo: src/pages/StockPage.js
 * Dossiê de Projeto e Colaboração: Ecossistema GoldenSisters
 * Versão: 2.3 – CORREÇÃO DE MAPEAMENTO (Campo 'stock')
 * Impacto da Versão: [src/pages/StockPage.js]
 * Data: 29/11/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - CORREÇÃO CRÍTICA: Mapeamento do campo de quantidade ajustado para 'data.stock' (baseado no Raio-X).
 * - CORREÇÃO: Mapeamento de 'productType' ("venda") para a flag visual "PRONTA".
 * - REMOVIDO: Modo de Inspeção/Debug (caixa azul e limite de 5 itens).
 * - RESTAURADO: Funcionalidade completa de filtros e paginação visual.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import {
  Package,
  AlertCircle,
  Loader,
  Filter,
  RotateCcw,
  Layers,
} from 'lucide-react';

const STOCK_TYPES = ['TODOS', 'BRUTA', 'PRONTA', 'EM_MONTAGEM', 'RESERVADA'];

const TYPE_LABELS = {
  BRUTA: 'Bruta',
  PRONTA: 'Pronta',
  EM_MONTAGEM: 'Em Montagem',
  RESERVADA: 'Reservada',
};

const TYPE_COLORS = {
  BRUTA: 'bg-yellow-50 text-yellow-800 border border-yellow-300',
  PRONTA: 'bg-emerald-50 text-emerald-800 border border-emerald-300',
  EM_MONTAGEM: 'bg-blue-50 text-blue-800 border border-blue-300',
  RESERVADA: 'bg-purple-50 text-purple-800 border border-purple-300',
};

// Mapeia o documento real (baseado no Raio-X) para a tela
const mapStockDoc = (snap) => {
  const data = snap.data() || {};

  // 1. Identificação
  // Se não tiver SKU, usa o ID como fallback visual
  const sku = data.sku || data.code || 'SEM-SKU';
  const name = data.name || data.description || '— Produto sem nome —';

  // 2. Classificação de Tipo
  // Se productType for "venda", consideramos peça PRONTA.
  let rawType = data.productType || data.itemType || 'venda';
  let normalizedType = 'PRONTA';

  if (rawType.toLowerCase() === 'bruta' || rawType.toLowerCase() === 'materia_prima') {
    normalizedType = 'BRUTA';
  } else if (rawType.toLowerCase() === 'montagem') {
    normalizedType = 'EM_MONTAGEM';
  }
  // (Padrão: se for 'venda', cai como PRONTA)

  // 3. Quantidades (A CORREÇÃO PRINCIPAL)
  const totalQty = Number(data.stock ?? 0); 
  
  // Como o JSON não mostrou campos explícitos de "reservado", assumimos 0 por enquanto
  // Se futuramente houver um campo "reservedStock", adicione aqui: data.reservedStock ?? 0
  const reservedQty = Number(data.reservedQuantity ?? 0);
  
  // Disponível é o Estoque Físico menos o Reservado
  const availableQty = Math.max(totalQty - reservedQty, 0);

  return {
    id: snap.id,
    sku,
    name,
    itemType: normalizedType, 
    totalQuantity: totalQty,
    reservedQuantity: reservedQty,
    availableQuantity: availableQty,
  };
};

export default function StockPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('TODOS');
  const [search, setSearch] = useState('');

  const fetchStock = async () => {
    setLoading(true);
    setError('');
    
    try {
      const colRef = collection(db, 'products'); 
      let qRef = colRef;

      try {
        qRef = query(colRef, orderBy('name'));
      } catch (err) {
        // Se falhar a ordenação por falta de índice, busca sem ordem
        qRef = colRef;
      }

      const snap = await getDocs(qRef);
      const list = snap.docs.map(mapStockDoc);
      setItems(list);

    } catch (err) {
      console.error('[StockPage] Erro ao carregar:', err);
      setError(
        'Não foi possível carregar os dados. Verifique o console.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStock();
  }, []);

  const filteredItems = useMemo(
    () =>
      items
        .filter((item) => {
          if (typeFilter === 'TODOS') return true;
          return item.itemType === typeFilter;
        })
        .filter((item) => {
          if (!search) return true;
          const term = search.trim().toLowerCase();
          return (
            (item.sku || '').toLowerCase().includes(term) ||
            (item.name || '').toLowerCase().includes(term)
          );
        }),
    [items, typeFilter, search]
  );

  const resumo = useMemo(() => {
    const base =
      filteredItems.length > 0 || search || typeFilter !== 'TODOS'
        ? filteredItems
        : items;

    const totalBruta = base
      .filter((i) => i.itemType === 'BRUTA')
      .reduce((sum, i) => sum + i.totalQuantity, 0);

    const totalPronta = base
      .filter((i) => i.itemType === 'PRONTA')
      .reduce((sum, i) => sum + i.totalQuantity, 0);

    const totalReservada = base.reduce(
      (sum, i) => sum + i.reservedQuantity,
      0
    );

    const totalDisponivel = base.reduce(
      (sum, i) => sum + i.availableQuantity,
      0
    );

    return {
      totalRegistros: base.length,
      totalBruta,
      totalPronta,
      totalReservada,
      totalDisponivel,
    };
  }, [items, filteredItems, search, typeFilter]);

  return (
    <div className="min-h-screen bg-white text-brand-black px-6 py-6">
      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="flex items-center text-2xl font-semibold text-brand-gold">
            <Package className="w-7 h-7 mr-3" />
            Estoque Técnico
          </h1>
          <p className="mt-1 text-sm text-gray-600 max-w-2xl">
            Visão unificada do estoque.
          </p>
        </div>

        <button
          type="button"
          onClick={fetchStock}
          className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:border-brand-gold hover:text-brand-gold bg-white shadow-sm transition-colors"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Atualizar
        </button>
      </div>

      {/* Resumo rápido */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            <Layers className="w-3 h-3" />
            Registros
          </p>
          <p className="text-2xl font-semibold text-gray-900">
            {resumo.totalRegistros}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            Total listado.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-1">Peças BRUTAS</p>
          <p className="text-2xl font-semibold text-gray-900">
            {resumo.totalBruta}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            Estoque técnico bruto.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-1">Peças PRONTAS</p>
          <p className="text-2xl font-semibold text-gray-900">
            {resumo.totalPronta}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            Produtos finalizados.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-1">Disponível Total</p>
          <p className="text-2xl text-gray-900 font-semibold">
             {resumo.totalDisponivel}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            Livre para uso.
          </p>
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Bloco principal – filtros + tabela */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        {/* Filtros */}
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Filter className="w-4 h-4" />
              <span>Filtros</span>
            </div>

            <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
              <div className="flex-1 md:w-64">
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold"
                  placeholder="Buscar por SKU ou nome..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="md:w-48">
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  {STOCK_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type === 'TODOS' ? 'Todos os tipos' : TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  SKU
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Peça / Descrição
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Reservado
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Disp.
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-gray-700 text-sm"
                  >
                    <div className="flex items-center justify-center gap-3">
                      <Loader className="w-5 h-5 animate-spin text-brand-gold" />
                      <span>Carregando dados...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filteredItems.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-gray-500 text-sm"
                  >
                    Nenhum item encontrado.
                  </td>
                </tr>
              )}

              {!loading &&
                filteredItems.map((item) => {
                  const badgeClass =
                    TYPE_COLORS[item.itemType] ||
                    'bg-gray-100 text-gray-700 border border-gray-300';

                  return (
                    <tr
                      key={item.id}
                      className="border-t border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 align-top text-xs font-mono text-gray-900">
                        {item.sku}
                      </td>
                      <td className="px-4 py-3 align-top text-sm text-gray-800">
                        {item.name}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${badgeClass}`}
                        >
                          {TYPE_LABELS[item.itemType]}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-center text-sm text-gray-900 font-medium">
                        {item.totalQuantity}
                      </td>
                      <td className="px-4 py-3 align-top text-center text-sm text-gray-500">
                        {item.reservedQuantity}
                      </td>
                      <td className="px-4 py-3 align-top text-center text-sm text-brand-gold font-bold">
                        {item.availableQuantity}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}